import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import {
  useCreateWebhookEndpoint,
  useDeactivateWebhookEndpoint,
  useWebhookEndpoints,
} from '@/api/queries';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const schema = z.object({
  url: z.string().url(),
  secret: z.string().min(16),
  events: z.string().min(1).default('*'),
});

type FormValues = z.infer<typeof schema>;

export function WebhookEndpointsPanel({ merchantId }: { merchantId: string }) {
  const { t } = useTranslation();
  const eps = useWebhookEndpoints(merchantId);
  const create = useCreateWebhookEndpoint(merchantId);
  const deactivate = useDeactivateWebhookEndpoint(merchantId);
  const [open, setOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { url: '', secret: '', events: '*' },
  });

  const onCreate = form.handleSubmit((values) =>
    create.mutate(
      {
        url: values.url,
        secret: values.secret,
        events: values.events.split(',').map((s) => s.trim()).filter(Boolean),
        active: true,
      },
      {
        onSuccess: () => {
          toast.success(t('webhook_endpoints.saved_toast') as string);
          form.reset();
          setOpen(false);
        },
        onError: (e) =>
          toast.error(e instanceof ApiError ? e.message : (t('common.failed') as string)),
      },
    ),
  );

  const onDeactivate = (id: string) => {
    if (!confirm(t('webhook_endpoints.deactivate_confirm') as string)) return;
    deactivate.mutate(id, {
      onSuccess: () => toast.success(t('webhook_endpoints.deactivated_toast') as string),
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : (t('common.failed') as string)),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold">{t('webhook_endpoints.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('webhook_endpoints.description')}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4" /> {t('webhook_endpoints.new')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={onCreate} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{t('webhook_endpoints.create_title')}</DialogTitle>
                <DialogDescription>{t('webhook_endpoints.description')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor="url">{t('webhook_endpoints.url_label')}</Label>
                <Input
                  id="url"
                  placeholder="https://merchant.example/webhooks/payhub"
                  {...form.register('url')}
                />
                {form.formState.errors.url && (
                  <p className="text-xs text-destructive">{form.formState.errors.url.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="secret">{t('webhook_endpoints.secret_label')}</Label>
                <Input
                  id="secret"
                  type="password"
                  autoComplete="off"
                  {...form.register('secret')}
                />
                {form.formState.errors.secret && (
                  <p className="text-xs text-destructive">{form.formState.errors.secret.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="events">{t('webhook_endpoints.events_label')}</Label>
                <Input id="events" placeholder="*" {...form.register('events')} />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {t('common.create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {eps.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('webhook_endpoints.cols.url')}</TableHead>
              <TableHead>{t('webhook_endpoints.cols.events')}</TableHead>
              <TableHead>{t('webhook_endpoints.cols.active')}</TableHead>
              <TableHead>{t('webhook_endpoints.cols.created')}</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {eps.data?.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-xs">{e.url}</TableCell>
                <TableCell className="text-xs">
                  {e.events.map((ev) => (
                    <Badge key={ev} variant="secondary" className="me-1">
                      {ev}
                    </Badge>
                  ))}
                </TableCell>
                <TableCell>
                  <Badge variant={e.active ? 'success' : 'outline'}>
                    {e.active ? t('common.active') : t('common.disabled')}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {dayjs(e.created_at).format('YYYY-MM-DD')}
                </TableCell>
                <TableCell className="text-end">
                  {e.active && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={deactivate.isPending}
                      onClick={() => onDeactivate(e.id)}
                    >
                      {t('common.deactivate')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!eps.isLoading && eps.data?.length === 0 && (
          <Empty title={t('webhook_endpoints.none')} className="border-0" />
        )}
      </div>
    </div>
  );
}
